import { AIModel, Message, Attachment } from '@/types';
import { generateId } from '@/lib/utils';
import { generateChatCompletion } from '@/lib/openai';
import { generateOllamaCompletion } from '@/lib/ollama';
import { generateGeminiCompletion } from '@/lib/gemini';
import { generateDeepseekCompletion } from '@/lib/deepseek';
import { generateCohereCompletion } from '@/lib/cohere';
import { generateMaplePTCompletion } from '@/lib/maplept';
import { generateClaudeCompletion } from '@/lib/claude';
import Papa from 'papaparse';
import { localLLM } from '@/llm/localTinyLlamaManager';
import { runTrustPrechecks, runTrustPostchecks, getTrustEnabled } from '@/lib/trust';
import { supabase } from '@/integrations/supabase/client';
import { toast } from '@/components/ui/use-toast';
import { gemma4Onnx, isElectronEnvironment } from '@/lib/gemma4Onnx';
import { extractThinkingSections } from '@/lib/chatThinking';

function isUUID(input: string): boolean {
  return /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(input);
}

export interface AIConfig {
  temperature?: number;
  max_tokens?: number;
  top_p?: number;
  enableThinking?: boolean;
  signal?: AbortSignal;
  /** When true, skip Trust Mode prechecks/postchecks for this request. */
  skipTrust?: boolean;
  usageContext?: {
    source?: string;
    feature?: string;
    runId?: string;
    runTitle?: string;
    phase?: string;
  };
}

export interface ModelThinking {
  enabled: boolean;
  status: 'none' | 'streaming' | 'complete' | 'unavailable';
  text?: string;
  providerExposed: boolean;
  supportsLiveStream?: boolean;
}

export interface ModelResponse {
  content: string;
  model: string;
  tokensUsed?: number;
  thinking?: ModelThinking;
}

interface MessageRequest {
  content: string | Array<{type: string, text?: string, image_url?: {url: string, detail: string}}>
  role: 'user' | 'assistant' | 'system';
  attachments?: Attachment[];
  chatId?: string;
}

const BILLABLE_PROVIDERS = new Set(['openai', 'claude', 'gemini', 'deepseek', 'cohere', 'maplept']);

function isBillableModel(model: AIModel) {
  return model.tier !== 'free' && BILLABLE_PROVIDERS.has(model.provider);
}

function buildThinkingResult(content: string, enabled: boolean): Pick<ModelResponse, 'content' | 'thinking'> {
  const parsed = extractThinkingSections(content);
  if (!parsed) {
    if (!enabled) {
      return { content };
    }

    return {
      content,
      thinking: {
        enabled: true,
        status: 'unavailable',
        providerExposed: false,
      },
    };
  }

  return {
    content: parsed.answerText,
    thinking: {
      enabled: true,
      status: 'complete',
      text: parsed.thinkingText,
      providerExposed: true,
    },
  };
}

function dispatchCreditEvent(target: Window, eventName: string, detail: any) {
  target.dispatchEvent(new CustomEvent(eventName, { detail }));
}

function notifyCreditState(eventName: string, detail: any) {
  if (typeof window === 'undefined') return;

  const targets = new Set<Window>([window]);
  try {
    if (window.top && window.top !== window) {
      targets.add(window.top);
    }
  } catch {
  }

  targets.forEach((target) => {
    dispatchCreditEvent(target, eventName, detail);
    dispatchCreditEvent(target, 'mapleos:credits-refresh', detail);
  });

  window.setTimeout(() => {
    targets.forEach((target) => {
      dispatchCreditEvent(target, 'mapleos:credits-refresh', detail);
    });
  }, 750);
}

function isAbortError(error: unknown): boolean {
  return Boolean(
    (error instanceof DOMException && error.name === 'AbortError') ||
    (typeof error === 'object' && error !== null && 'name' in error && (error as { name?: string }).name === 'AbortError')
  );
}

function throwIfAborted(signal?: AbortSignal) {
  if (!signal?.aborted) {
    return;
  }

  throw new DOMException('The request was aborted.', 'AbortError');
}

async function getFunctionAuthHeaders() {
  const { data } = await supabase.auth.getSession();
  const token = data.session?.access_token;

  return token
    ? {
        Authorization: `Bearer ${token}`,
      }
    : undefined;
}

function estimateBillableTokens(messages: Message[], responseContent: string) {
  const messageCharacters = messages.reduce((total, message) => {
    if (typeof message.content === 'string') {
      return total + message.content.length;
    }

    if (Array.isArray(message.content)) {
      const contentParts = message.content as Array<{ text?: string }>;
      return total + contentParts.reduce((partTotal, part) => partTotal + (part.text?.length || 0), 0);
    }

    return total;
  }, 0);

  const responseCharacters = responseContent.length;
  return Math.max(1, Math.ceil((messageCharacters + responseCharacters) / 4));
}

async function getCreditAccountSnapshot() {
  const headers = await getFunctionAuthHeaders();
  const { data, error } = await supabase.functions.invoke('get-ai-credit-account', {
    headers,
  });
  if (error) throw error;
  return data;
}

async function recordBillableUsage(
  messages: Message[],
  model: AIModel,
  tokensUsed: number,
  usageContext?: AIConfig['usageContext']
) {
  if (!tokensUsed || !isBillableModel(model)) return;

  const chatId = messages.find((message) => message.chatId)?.chatId;
  const headers = await getFunctionAuthHeaders();
  const { data, error } = await supabase.functions.invoke('track-ai-usage', {
    body: {
      provider: model.provider,
      model: model.name,
      modelTier: model.tier,
      tokensUsed,
      chatId,
      isWebLLM: model.provider === 'local',
      isFreeModel: model.tier === 'free',
      source: usageContext?.source,
      feature: usageContext?.feature,
      runId: usageContext?.runId,
      runTitle: usageContext?.runTitle,
      phase: usageContext?.phase,
    },
    headers,
  });

  if (error) throw error;

  if (data?.exceeded) {
    notifyCreditState('mapleos:credits-exceeded', data);
  } else if (data?.low) {
    notifyCreditState('mapleos:credits-low', data);
  } else {
    notifyCreditState('mapleos:credits-refresh', data);
  }
}

export abstract class ModelProvider {
  abstract sendMessage(
    messages: MessageRequest[],
    model: AIModel,
    config?: AIConfig
  ): Promise<ModelResponse>;
}

export class OpenAIProvider extends ModelProvider {
  async sendMessage(messages: MessageRequest[], model: AIModel, config?: AIConfig): Promise<ModelResponse> {
    try {
      const modelNameToUse = model.apiName || model.id;
      const thinkingEnabled = Boolean(config?.enableThinking && model.supports_reasoning);

      const openaiMessages = messages.map((msg) => {
        const content: any[] = [];

        if (typeof msg.content === 'string' && msg.content.trim()) {
          content.push({ type: 'text', text: msg.content.trim() });
        } else if (Array.isArray(msg.content)) {
          content.push(...msg.content);
        }

        if (msg.attachments?.length) {
          for (const att of msg.attachments) {
            if ((att.type === 'image' || att.type === 'canvas') && att.content) {
              content.push({
                type: 'image_url',
                image_url: {
                  url: att.content,
                  detail: 'auto'
                }
              });
            } else if (att.type === 'file' && att.content) {
              content.push({
                type: 'text',
                text: `The user attached a file named "${att.name}". Here is the content:\n\n${att.content}`
              });
            } else if (att.type === 'audio' && att.content) {
              content.push({
                type: 'text',
                text: `The user attached an audio clip${att.name ? ` named "${att.name}"` : ''}.`
              });
            }
          }
        }

        if (content.length === 0) return { role: msg.role, content: '' };
        if (content.length === 1 && content[0].type === 'text') {
          return { role: msg.role, content: content[0].text };
        }

        return { role: msg.role, content };
      });

      const response = await generateChatCompletion(openaiMessages, modelNameToUse, undefined, config);
      const thinkingResult = buildThinkingResult(response.content, thinkingEnabled);
      return {
        content: thinkingResult.content,
        model: model.id,
        tokensUsed: response.tokensUsed,
        thinking: thinkingResult.thinking,
      };

    } catch (error: any) {
      console.error('Error in OpenAIProvider:', error);
      throw new Error(error.message || 'OpenAIProvider error');
    }
  }
}

export class ClaudeProvider extends ModelProvider {
  async sendMessage(messages: MessageRequest[], model: AIModel, config?: AIConfig): Promise<ModelResponse> {
    try {
      const modelNameToUse = model.apiName || model.id;
      const thinkingEnabled = Boolean(config?.enableThinking && model.supports_reasoning);
      const system = messages
        .filter((message) => message.role === 'system')
        .map((message) => {
          if (typeof message.content === 'string') {
            return message.content.trim();
          }

          if (Array.isArray(message.content)) {
            return message.content
              .map((part: any) => (part?.type === 'text' && typeof part?.text === 'string' ? part.text : ''))
              .filter(Boolean)
              .join('\n')
              .trim();
          }

          return '';
        })
        .filter(Boolean)
        .join('\n\n');

      const claudeMessages = messages
        .filter((message) => message.role !== 'system')
        .map((message) => {
          const content: Array<any> = [];

          if (typeof message.content === 'string' && message.content.trim()) {
            content.push({ type: 'text', text: message.content.trim() });
          } else if (Array.isArray(message.content)) {
            for (const part of message.content as Array<any>) {
              if (part?.type === 'text' && typeof part.text === 'string' && part.text.trim()) {
                content.push({ type: 'text', text: part.text.trim() });
                continue;
              }

              if (part?.type === 'image_url' && part.image_url?.url) {
                const imageUrl = part.image_url.url as string;
                const dataUrlMatch = imageUrl.match(/^data:(image\/[a-zA-Z0-9.+-]+);base64,(.+)$/);

                if (dataUrlMatch) {
                  content.push({
                    type: 'image',
                    source: {
                      type: 'base64',
                      media_type: dataUrlMatch[1],
                      data: dataUrlMatch[2],
                    },
                  });
                } else if (/^https?:\/\//i.test(imageUrl)) {
                  content.push({
                    type: 'image',
                    source: {
                      type: 'url',
                      url: imageUrl,
                    },
                  });
                } else {
                  content.push({ type: 'text', text: `Image provided: ${imageUrl}` });
                }
              }
            }
          }

          if (message.attachments?.length) {
            for (const attachment of message.attachments) {
              if ((attachment.type === 'image' || attachment.type === 'canvas') && attachment.content) {
                const dataUrlMatch = attachment.content.match(/^data:(image\/[a-zA-Z0-9.+-]+);base64,(.+)$/);

                if (dataUrlMatch) {
                  content.push({
                    type: 'image',
                    source: {
                      type: 'base64',
                      media_type: dataUrlMatch[1],
                      data: dataUrlMatch[2],
                    },
                  });
                } else if (/^https?:\/\//i.test(attachment.content)) {
                  content.push({
                    type: 'image',
                    source: {
                      type: 'url',
                      url: attachment.content,
                    },
                  });
                } else {
                  content.push({ type: 'text', text: `Image provided: ${attachment.content}` });
                }
              } else if (attachment.type === 'file' && attachment.content) {
                content.push({
                  type: 'text',
                  text: `The user attached a file named "${attachment.name}". Here is the content:\n\n${attachment.content}`,
                });
              } else if (attachment.type === 'audio' && attachment.content) {
                content.push({
                  type: 'text',
                  text: `The user attached an audio clip${attachment.name ? ` named "${attachment.name}"` : ''}.`,
                });
              }
            }
          }

          return {
            role: message.role === 'assistant' ? 'assistant' as const : 'user' as const,
            content: content.length === 1 && content[0].type === 'text' ? content[0].text : content,
          };
        });

      const response = await generateClaudeCompletion(claudeMessages, modelNameToUse, system || undefined, config);

      if (thinkingEnabled && response.providerExposedThinking && response.thinkingText?.trim()) {
        return {
          content: response.content,
          model: model.id,
          tokensUsed: response.tokensUsed,
          thinking: {
            enabled: true,
            status: 'complete',
            text: response.thinkingText,
            providerExposed: true,
          },
        };
      }

      const thinkingResult = buildThinkingResult(response.content, thinkingEnabled);
      return {
        content: thinkingResult.content,
        model: model.id,
        tokensUsed: response.tokensUsed,
        thinking: thinkingResult.thinking,
      };
    } catch (error: any) {
      console.error('Error in ClaudeProvider:', error);
      throw new Error(error.message || 'ClaudeProvider error');
    }
  }
}

export class OllamaProvider extends ModelProvider {
  async sendMessage(
    messages: MessageRequest[],
    model: AIModel,
    config?: AIConfig
  ): Promise<ModelResponse> {
    try {
      const source: 'local' | 'remote' = (model as any).source || 'local';
      const modelName = model.apiName || model.id;
      const thinkingEnabled = Boolean(config?.enableThinking && model.supports_reasoning);

      if (model.provider !== 'ollama') {
        throw new Error('OllamaProvider called with non-ollama model');
      }

      const isImageInMessages = messages.some((msg) => {
        if (Array.isArray(msg.content)) {
          return msg.content.some(
            (part) =>
              part &&
              typeof part === 'object' &&
              part.type === 'image_url' &&
              part.image_url?.url
          );
        }
        if (msg.attachments?.some(att => att.type === 'image' || att.type === 'canvas')) {
          return true;
        }
        return false;
      });
      
      const knownNonVisionModels = ['llama3', 'mistral', 'gemma'];
      const isNonVisionModel = knownNonVisionModels.includes(model.apiName?.toLowerCase());
      
      if (isImageInMessages && isNonVisionModel) {
        console.warn(`[OllamaProvider] Model ${model.apiName} is non-vision and cannot process image attachments.`);
        return {
          content: `⚠️ The selected model (${model.apiName}) does not support image analysis. Please switch to a vision-capable model like DeepSeek or GPT-4.`,
          model: model.id,
          tokensUsed: 0,
          thinking: thinkingEnabled ? { enabled: true, status: 'unavailable', providerExposed: false } : undefined,
        };
      }

      const processedMessages = messages.map(msg => {
        let textContent = '';
        const images: string[] = [];

        if (Array.isArray(msg.content)) {
          msg.content.forEach(part => {
            if (part.type === 'text' && part.text) {
              textContent += part.text + ' ';
            } else if (part.type === 'image_url' && part.image_url?.url?.startsWith('data:')) {
              images.push(part.image_url.url.split(',')[1]);
            }
          });
        } else if (typeof msg.content === 'string') {
          textContent = msg.content;
        }

        if (msg.attachments?.length) {
          msg.attachments.forEach(att => {
            if (att.type === 'file' && att.content) {
              textContent += `\n\n--- FILE ATTACHMENT ---\nFilename: ${att.name}\nContent:\n${att.content}\n--- END OF FILE ---\n`;
            } else if (att.type === 'audio') {
              textContent += `\n\n[Audio attachment${att.name ? `: ${att.name}` : ''}]`;
            }
          });
        }

        return {
          role: msg.role,
          content: textContent.trim(),
          images,
        };
      });

      const response = await generateOllamaCompletion(processedMessages, modelName, source, config);
      const thinkingResult = buildThinkingResult(response.content, thinkingEnabled);

      return {
        content: thinkingResult.content,
        model: model.id,
        tokensUsed: response.tokensUsed,
        thinking: thinkingResult.thinking,
      };

    } catch (error: any) {
      console.error('Error in Ollama provider:', error);
      let errorMessage = 'An error occurred while communicating with Ollama.';
      if (error.message.includes('URL')) {
        errorMessage = 'Invalid Ollama API URL. Please check your Ollama settings.';
      } else if (
        error.message.includes('model not found') ||
        error.message.includes('no model')
      ) {
        errorMessage = `Model error: ${error.message}. Please make sure the model is available in your Ollama instance.`;
      } else if (
        error.message.includes('Network') ||
        error.message.includes('Failed to fetch') ||
        error.name === 'AbortError' ||
        error.message.includes('timeout') ||
        error.message.includes('ECONNREFUSED')
      ) {
        errorMessage = 'Network error or request timeout. Please check if your Ollama server is running and accessible.';
      } else if (error.message.includes('Invalid JSON') || error.message.includes('Unexpected token')) {
        errorMessage = 'Received an invalid response from Ollama. This may be due to version incompatibility.';
      }
      throw new Error(errorMessage);
    }
  }
}

export class GeminiProvider extends ModelProvider {
  async sendMessage(
    messages: MessageRequest[],
    model: AIModel,
    config?: AIConfig
  ): Promise<ModelResponse> {
    try {
      const modelName = model.apiName || model.id;
      const chatId = messages.length > 0 ? messages[0].chatId : undefined;
      const thinkingEnabled = Boolean(config?.enableThinking && model.supports_reasoning);
      const processedMessages = messages.map(msg => {
         const parts: Array<{ text?: string, inline_data?: { mime_type: string, data: string } }> = [];
         if (typeof msg.content === 'string' && msg.content) {
           parts.push({ text: msg.content });
         } else if (Array.isArray(msg.content)) {
            (msg.content as Array<any>).forEach(part => {
              if (part.type === 'text' && part.text) {
                parts.push({ text: part.text });
              } else if (part.type === 'image_url' && part.image_url?.url) {
                 parts.push({
                   inline_data: {
                     mime_type: 'image/jpeg',
                     data: part.image_url.url.startsWith('data:') ? part.image_url.url.split(',')[1] : part.image_url.url
                   }
                 });
              }
            });
         }
         const role = msg.role === 'assistant' ? 'model' : 'user';
         return { role, parts };
      }).filter(msg => msg.parts.length > 0);
      const response = await generateGeminiCompletion(processedMessages, modelName, chatId, config);
      const thinkingResult = buildThinkingResult(response.content, thinkingEnabled);
      return { 
        content: thinkingResult.content, 
        model: model.id,
        tokensUsed: response.tokensUsed,
        thinking: thinkingResult.thinking,
      };
    } catch (error: any) {
      console.error('Error in Gemini provider:', error);
      throw new Error(`Gemini API error: ${error.message}`);
    }
  }
}

export class DeepSeekProvider extends ModelProvider {
  async sendMessage(
    messages: MessageRequest[],
    model: AIModel,
    config?: AIConfig
  ): Promise<ModelResponse> {
    try {
      const modelName = model.apiName || model.id;
      const thinkingEnabled = Boolean(config?.enableThinking && model.supports_reasoning);
      const processedMessages = messages.map(msg => ({
         role: msg.role,
         content: typeof msg.content === 'string' ? msg.content : ''
      }));
      const response = await generateDeepseekCompletion(processedMessages, modelName, config);
      const thinkingResult = buildThinkingResult(response.content, thinkingEnabled);

      return { 
        content: thinkingResult.content, 
        model: model.id,
        tokensUsed: response.tokensUsed,
        thinking: thinkingResult.thinking,
      };
    } catch (error: any) {
      console.error('Error in DeepSeek provider:', error);
      throw new Error(`DeepSeek API error: ${error.message}`);
    }
  }
}

export class CohereProvider extends ModelProvider {
  async sendMessage(
    messages: MessageRequest[],
    model: AIModel,
    config?: AIConfig
  ): Promise<ModelResponse> {
    try {
      const modelName = model.apiName || model.id;
      const thinkingEnabled = Boolean(config?.enableThinking && model.supports_reasoning);
      const processedMessages = messages.map(msg => ({
         role: msg.role,
         content: typeof msg.content === 'string' ? msg.content : ''
      }));
      const response = await generateCohereCompletion(processedMessages, modelName, config);
      const thinkingResult = buildThinkingResult(response.content, thinkingEnabled);

      return { 
        content: thinkingResult.content, 
        model: model.id,
        tokensUsed: response.tokensUsed,
        thinking: thinkingResult.thinking,
      };
    } catch (error: any) {
      console.error('Error in Cohere provider:', error);
      throw new Error(`Cohere API error: ${error.message}`);
    }
  }
}

export class MaplePTProvider extends ModelProvider {
  async sendMessage(
    messages: MessageRequest[],
    model: AIModel,
    config?: AIConfig
  ): Promise<ModelResponse> {
    try {
      const modelNameToUse = model.apiName || model.id;
      const useDefaultModel = !modelNameToUse || !modelNameToUse.includes('/mnt/');
      const thinkingEnabled = Boolean(config?.enableThinking && model.supports_reasoning);

      const mapleMessages = messages.map((msg) => {
        let textContent = '';

        if (typeof msg.content === 'string' && msg.content.trim()) {
          textContent += msg.content.trim();
        } else if (Array.isArray(msg.content)) {
          (msg.content as Array<any>).forEach(part => {
            if (part?.type === 'text' && part.text) {
              textContent += (textContent ? '\n\n' : '') + part.text;
            } else if (part?.type === 'image_url' && part.image_url?.url) {
              textContent += (textContent ? '\n\n' : '') + `Image provided: ${part.image_url.url}`;
            }
          });
        }

        if (msg.attachments?.length) {
          for (const att of msg.attachments) {
            if ((att.type === 'image' || att.type === 'canvas') && att.content) {
              textContent += (textContent ? '\n\n' : '') + `Image provided: ${att.content}`;
            } else if (att.type === 'file' && att.content) {
              textContent += (textContent ? '\n\n' : '') + `File "${att.name}" content:\n${att.content}`;
            } else if (att.type === 'audio') {
              textContent += (textContent ? '\n\n' : '') + `Audio attachment${att.name ? `: ${att.name}` : ''}`;
            }
          }
        }

        return { role: msg.role, content: textContent };
      });

      const response = await generateMaplePTCompletion(mapleMessages, useDefaultModel ? '' : modelNameToUse, config);
      const thinkingResult = buildThinkingResult(response.content, thinkingEnabled);
      return {
        content: thinkingResult.content,
        model: model.id,
        tokensUsed: response.tokensUsed,
        thinking: thinkingResult.thinking,
      };
    } catch (error: any) {
      console.error('Error in MaplePTProvider:', error);
      throw new Error(error.message || 'MaplePTProvider error');
    }
  }
}

export class LocalProvider extends ModelProvider {
  async sendMessage(messages: MessageRequest[], model: AIModel, config?: AIConfig): Promise<ModelResponse> {
    const thinkingEnabled = Boolean(config?.enableThinking && model.supports_reasoning);

    const normalizeContent = (msg: MessageRequest): string => {
      const parts: string[] = [];
      if (typeof msg.content === 'string' && msg.content.trim()) {
        parts.push(msg.content.trim());
      } else if (Array.isArray(msg.content)) {
        msg.content.forEach((part: any) => {
          if (part?.type === 'text' && part.text) parts.push(part.text.trim());
          else if (part?.type === 'image_url' && part.image_url?.url) parts.push(`Image provided: ${part.image_url.url}`);
        });
      }
      if (msg.attachments?.length) {
        msg.attachments.forEach(att => {
          if ((att.type === 'image' || att.type === 'canvas') && att.content) {
            parts.push(`Image provided: ${att.content}`);
          } else if (att.type === 'file' && att.content) {
            parts.push(`File "${att.name}" content:\n${att.content}`);
          } else if (att.type === 'code' && att.content) {
            parts.push(`Code "${att.name || ''}":\n${att.content}`);
          } else if (att.type === 'audio') {
            parts.push(`Audio attachment${att.name ? `: ${att.name}` : ''}`);
          }
        });
      }
      return parts.join('\n\n').split('\n').filter(l => !/^\s*(USER|ASSISTANT|SYSTEM):/i.test(l)).join('\n').trim();
    };

    const userIndices = messages
      .map((m, i) => ({ i, isUser: m.role === 'user' }))
      .filter(x => x.isUser)
      .map(x => x.i);

    const lastUserIdx = userIndices.length ? userIndices[userIndices.length - 1] : messages.length - 1;
    const lastUserMsg = messages[lastUserIdx];
    const lastUserText = normalizeContent(lastUserMsg);

    const conversationHistory = messages
      .map((m, i) => {
        if (i === lastUserIdx) return null;
        const text = normalizeContent(m);
        if (!text) return null;
        const role = m.role === 'assistant' ? 'Assistant' : m.role === 'system' ? 'System' : 'User';
        return `${role}: ${text}`;
      })
      .filter(Boolean)
      .join('\n\n');

    const wantsDetail = !!lastUserText && /tell me more|more detail|elaborate|expand|in depth|deep dive|explain more|details|comprehensive|longer/i.test(lastUserText);
    const wantsStructured = !!lastUserText && /summary|overview|outline|structured|structure/i.test(lastUserText);
    const isGreeting = !!lastUserText && /^\s*(hi|hello|hey|howdy|yo|hiya|good\s+(morning|afternoon|evening))\b[\s.!?]*$/i.test(lastUserText);
    const identityInstruction = [
      `You are the currently selected local model: ${model.name}.`,
      `Your runtime model identifier is: ${model.apiName || model.id}.`,
      'Answer the user\'s actual request directly and naturally.',
      'Return exactly one assistant reply.',
      'Do not continue the dialogue for the user.',
      'Do not invent additional speakers, stage directions, or transcript-style lines.',
      'Only mention your model identity when the user asks about it or when it is directly relevant.',
      'Do not claim to be a different model just because another model name appears in prior conversation history.',
    ].join('\n');

    if (isGreeting) {
      return {
        content: 'Hello! How can I help today?',
        model: model.id,
        tokensUsed: 7,
      };
    }

    const prompt = [
      identityInstruction,
      conversationHistory ? `Conversation history:\n${conversationHistory}` : "",
      thinkingEnabled
        ? [
            'Think step by step before answering and format the response exactly with these markdown headings:',
            '## Thinking',
            '- Brief reasoning steps here',
            '## Answer',
            '- Final answer here',
          ].join('\n')
        : (wantsDetail || wantsStructured)
          ? [
              "Create a well-structured answer with clear headings. Use this template and adapt to the topic:",
              "Summary: 2–3 sentences overview.",
              "Key Facts: 4–7 bullet points with specific data, names, or figures.",
              "Regions/Components (if relevant): brief bullets or short paragraphs.",
              "Economy & Government (if relevant): 2–4 bullets.",
              "Culture & Society (if relevant): 2–4 bullets.",
              "Next Steps / Further Reading: 1–3 suggestions.",
              "Write in plain text with simple headings; avoid role labels.",
              "Do not write extra dialogue turns or greetings unless the user asked for them."
            ].join('\n')
          : "Provide a helpful, clear answer. Do not add role labels, stage directions, or extra conversation turns.",
      `User message:\n${lastUserText || "Hello"}`
    ].filter(Boolean).join('\n\n');

    if (!localLLM.isReady(model)) {
      await localLLM.init(model);
    }

    if (!localLLM.isReady(model)) {
      throw new Error('Local LLM not ready (WebGPU unavailable or model not initialized).');
    }

    let full = '';
    const generateOnce = async (p: string, temperatureOverride?: number) => {
      full = '';
      const result = await localLLM.generate({
        prompt: p,
        maxTokens: config?.max_tokens ?? (thinkingEnabled ? 1536 : ((wantsDetail || wantsStructured) ? 1536 : 1024)),
        temperature: temperatureOverride ?? (config?.temperature ?? 0.75),
        topP: config?.top_p ?? 0.9,
        stopSequences: ["\nUSER:", "\nASSISTANT:", "\nSYSTEM:", "\nUser:", "\nAssistant:", "\nSystem:"],
        onToken: (t) => { full += t; },
      });
      return result || full;
    };

    let text = await generateOnce(prompt);

    const tooShort = (t: string) => !t || t.trim().length < ((wantsDetail || wantsStructured || thinkingEnabled) ? 120 : 40) || /^none\.?$/i.test(t.trim());
    if (tooShort(text)) {
      const retryPrompt = [
        prompt,
        thinkingEnabled
          ? 'Retry and strictly return both markdown headings: "## Thinking" followed by "## Answer".'
          : (wantsDetail || wantsStructured)
            ? "Expand with a detailed, structured overview: include Summary, Key Facts, Regions/Components (if relevant), Economy & Government (if relevant), Culture & Society (if relevant), and Next Steps."
            : "Please provide a brief but informative response in 3–6 sentences.",
        'Do not continue the dialogue or simulate the user replying.'
      ].join('\n\n');
      text = await generateOnce(retryPrompt, 0.8);
    }

    const cleaned = text
      .split('\n')
      .filter((line) => !/^\s*(USER|ASSISTANT|SYSTEM):/i.test(line))
      .join('\n')
      .trim();

    const thinkingResult = buildThinkingResult(cleaned || text, thinkingEnabled);

    return {
      content: thinkingResult.content,
      model: model.id,
      tokensUsed: Math.ceil((cleaned || text).length / 4),
      thinking: thinkingResult.thinking,
    };
  }
}

export class GemmaOnnxProvider extends ModelProvider {
  async sendMessage(messages: MessageRequest[], model: AIModel, config?: AIConfig): Promise<ModelResponse> {
    try {
      const thinkingEnabled = Boolean(config?.enableThinking && model.supports_reasoning);

      await gemma4Onnx.load({
        modelId: model.apiName,
        dtype: 'q4f16',
        device: 'webgpu',
      });

      const response = await gemma4Onnx.generate({
        messages,
        config,
      });

      const thinkingResult = buildThinkingResult(response.content, thinkingEnabled);

      return {
        content: thinkingResult.content,
        model: model.id,
        tokensUsed: response.tokensUsed,
        thinking: thinkingResult.thinking,
      };
    } catch (error: any) {
      throw new Error(error?.message || 'Gemma 4 ONNX failed to load or generate a response.');
    }
  }
}

function getGemmaFallbackModel(currentModel: AIModel, appModels: AIModel[]) {
  const localModel = appModels.find((model) => model.provider === 'local' && model.id !== currentModel.id);
  if (localModel) return localModel;

  const localOllama = appModels.find((model) => model.provider === 'ollama' && (model as any).source === 'local' && model.id !== currentModel.id);
  if (localOllama) return localOllama;

  const mapleptModel = appModels.find((model) => model.provider === 'maplept' && model.id !== currentModel.id);
  if (mapleptModel) return mapleptModel;

  const claudeModel = appModels.find((model) => model.provider === 'claude' && model.id !== currentModel.id);
  if (claudeModel) return claudeModel;

  return appModels.find((model) => model.id !== currentModel.id) || null;
}

export function getModelProvider(model: AIModel, appModels: AIModel[]): ModelProvider {
  switch (model.provider) {
    case 'openai':
      return new OpenAIProvider();
    case 'claude':
      return new ClaudeProvider();
    case 'ollama':
      return new OllamaProvider();
    case 'gemini':
      return new GeminiProvider();
    case 'deepseek':
      return new DeepSeekProvider();
    case 'cohere':
      return new CohereProvider();
    case 'maplept':
      return new MaplePTProvider();
    case 'local':
      return new LocalProvider();
    case 'transformers':
      return new GemmaOnnxProvider();
    case 'custom':
      throw new Error(`Custom provider not implemented yet`);
    default:
      console.warn(`Unknown model provider: ${model.provider}. Falling back to OpenAI.`);
      const hostedModel = appModels.find(m => m.provider === 'openai' || m.provider === 'claude');
      if (hostedModel) {
         return hostedModel.provider === 'claude' ? new ClaudeProvider() : new OpenAIProvider();
      }
      const ollamaModel = appModels.find(m => m.provider === 'ollama');
      if (ollamaModel) {
         return new OllamaProvider();
      }
      throw new Error(`Provider ${model.provider} not supported and no fallback available`);
  }
}

export async function sendMessageToModel(
  messages: Message[],
  model: AIModel,
  appModels: AIModel[],
  config?: AIConfig,
  isToolCheck?: boolean
): Promise<ModelResponse> {
  try {
    console.log("sendMessageToModel: Starting with model", model.name, "and provider", model.provider);

    throwIfAborted(config?.signal);
    const lastUserActionMsDiff = null;

    const pre = config?.skipTrust
      ? { allow: true as const, messages, model, meta: { enabled: false, authorship: 1, intent: 1, presence: 1, trustScore: 1, trustFloor: 0, simulationCollapsed: false, redactionsCount: 0, policyVersion: 'v1', actionTaken: 'allow' as const } }
      : await runTrustPrechecks(messages, model, appModels, lastUserActionMsDiff);

    if (!pre.allow) {
      try {
        await supabase.functions.invoke('log-client-events', {
          body: {
            category: 'trust',
            trust_enabled: pre.meta.enabled,
            trust_score: pre.meta.trustScore,
            trust_floor: pre.meta.trustFloor,
            authorship_score: pre.meta.authorship,
            intent_score: pre.meta.intent,
            presence_score: pre.meta.presence,
            simulation_collapsed: pre.meta.simulationCollapsed,
            redactions_count: pre.meta.redactionsCount,
            model_id: model.id,
            model_provider: model.provider,
            action_taken: pre.meta.actionTaken || 'blocked',
            policy_version: pre.meta.policyVersion,
            created_at: new Date().toISOString()
          }
        });
      } catch (e) {
        console.warn('[Trust] Failed to log blocked event:', e);
      }

      return {
        content: pre.complianceMessage || "Blocked by EHCO Trust Mode.",
        model: model.apiName || model.id,
        tokensUsed: 0,
      };
    }

    throwIfAborted(config?.signal);

    let effectiveModel = pre.model;
    const effectiveConfig: AIConfig = {

      ...(config || {}),
      ...(pre.caps || {}),
      enableThinking: Boolean(config?.enableThinking && pre.model.supports_reasoning),
    };

    if (effectiveModel.provider === 'local' && !localLLM.isReady(effectiveModel)) {
      try {
        await localLLM.init(effectiveModel);
      } catch (localInitError) {
        const ollamaLocal = appModels.find(m => m.provider === 'ollama' && (m as any).source === 'local');
        const ollamaAny = appModels.find(m => m.provider === 'ollama');
        const mapleptAny = appModels.find(m => m.provider === 'maplept');
        const claudeAny = appModels.find(m => m.provider === 'claude');
        const fallback = ollamaLocal || ollamaAny || mapleptAny || claudeAny || appModels[0];

        if (fallback) {
          console.warn("[ModelService] Local LLM not ready; switching to:", fallback.name, "(", fallback.provider, ")");
          effectiveModel = fallback;
          effectiveConfig.enableThinking = Boolean(config?.enableThinking && fallback.supports_reasoning);
        } else {
          throw localInitError;
        }
      }
    }

    if (isBillableModel(effectiveModel)) {
      const creditSnapshot = await getCreditAccountSnapshot();
      if (creditSnapshot?.availableCredits <= 0 && !creditSnapshot?.canAutoTopup) {
        notifyCreditState('mapleos:credits-exceeded', creditSnapshot);
        return {
          content: 'You have exceeded your MapleOS AI credits. Open AI Credits to top up or enable auto top-up before using paid AI providers again.',
          model: effectiveModel.id,
          tokensUsed: 0,
        };
      }
    }

    let provider = getModelProvider(effectiveModel, appModels);
    const requestMessages = messages.map(msg => ({
      content: msg.content,
      role: msg.role,
      attachments: msg.attachments,
      chatId: msg.chatId
    }));

    const requireCitations = getTrustEnabled() && !config?.skipTrust && (localStorage.getItem('trust_require_citations') === 'true');
    if (requireCitations) {
      const citationRule = {
        role: 'system' as const,
        content:
          "When external context or prior knowledge is used, include citations with IDs or URLs. If no relevant context exists, explicitly state: 'No context found.'",
      };
      // @ts-ignore
      requestMessages.unshift(citationRule);
    }

    if (isToolCheck) {
      const toolPrompt = {
        role: 'system' as const,
        content: `You have access to the following tools. If you need to use a tool, respond ONLY with a JSON object with 'tool' and 'args' keys.
- \`search_web(query: string)\`
- \`scrape_url(url: string)\`

Example: {"tool": "search_web", "args": {"query": "latest AI news"}}
If you don't need a tool, just respond to the user as usual.`,
      };
      // @ts-ignore
      requestMessages.unshift(toolPrompt);
    }

    let response: ModelResponse;
    try {
      throwIfAborted(effectiveConfig.signal);
      response = await provider.sendMessage(requestMessages, effectiveModel, effectiveConfig);
      throwIfAborted(effectiveConfig.signal);
    } catch (providerError: any) {

      if (effectiveModel.provider !== 'transformers') {
        throw providerError;
      }

      const fallbackModel = getGemmaFallbackModel(effectiveModel, appModels);
      if (!fallbackModel) {
        throw providerError;
      }

      const gemmaErrorMessage = providerError instanceof Error
        ? providerError.message
        : typeof providerError?.message === 'string'
          ? providerError.message
          : typeof providerError === 'string'
            ? providerError
            : 'Unknown Gemma runtime error.';

      console.warn('[ModelService] Gemma 4 ONNX failed; falling back to:', fallbackModel.name, providerError);
      toast({
        title: 'Experimental model fallback',
        description: `Gemma 4 error: ${gemmaErrorMessage} Falling back to ${fallbackModel.name}.`,
      });

      effectiveModel = fallbackModel;
      effectiveConfig.enableThinking = Boolean(config?.enableThinking && fallbackModel.supports_reasoning);

      provider = getModelProvider(effectiveModel, appModels);
      throwIfAborted(effectiveConfig.signal);
      response = await provider.sendMessage(requestMessages, effectiveModel, effectiveConfig);
      throwIfAborted(effectiveConfig.signal);
    }
    console.log("sendMessageToModel: Received response", response);

    let finalContent = response.content;
    let thinking = response.thinking;
    let masked = false;
    let postRedactions = 0;

    if (getTrustEnabled() && !config?.skipTrust && finalContent) {
      const { safeContent, masked: outMasked, redactionsCount } = runTrustPostchecks(finalContent);
      finalContent = safeContent;
      masked = outMasked;
      postRedactions = redactionsCount;
    }

    if (getTrustEnabled() && !config?.skipTrust && thinking?.text) {
      const { safeContent } = runTrustPostchecks(thinking.text);
      thinking = {
        ...thinking,
        text: safeContent,
      };
    }

    try {
      await supabase.functions.invoke('log-client-events', {
        body: {
          category: 'trust',
          trust_enabled: pre.meta.enabled,
          trust_score: pre.meta.trustScore,
          trust_floor: pre.meta.trustFloor,
          authorship_score: pre.meta.authorship,
          intent_score: pre.meta.intent,
          presence_score: pre.meta.presence,
          simulation_collapsed: pre.meta.simulationCollapsed,
          redactions_count: pre.meta.redactionsCount,
          post_redactions_count: postRedactions,
          model_id: effectiveModel.id,
          model_provider: effectiveModel.provider,
          action_taken: masked ? 'masked_response' : (pre.meta.actionTaken || 'allow'),
          policy_version: pre.meta.policyVersion,
          created_at: new Date().toISOString()
        }
      });
    } catch (e) {
      console.warn('[Trust] Failed to log allow event:', e);
    }

    try {
      const billableTokensUsed = isBillableModel(effectiveModel)
        ? (response.tokensUsed && response.tokensUsed > 0
            ? response.tokensUsed
            : estimateBillableTokens(messages, finalContent || response.content || ''))
        : response.tokensUsed || 0;

      await recordBillableUsage(messages, effectiveModel, billableTokensUsed, config?.usageContext);
    } catch (usageError) {
      console.warn('[ModelService] Failed to record billable usage:', usageError);
    }

    return {
      content: finalContent,
      model: effectiveModel.id,
      tokensUsed: response.tokensUsed,
      thinking,
    };
  } catch (error: any) {
    if (isAbortError(error)) {
      throw error;
    }

    console.error('sendMessageToModel: Error sending message to model:', error);
    if (
      error.message.includes('Network') ||

      error.message.includes('temporarily unavailable') || 
      error.message.includes('service') ||
      error.message.includes('Too many requests') ||
      error.message.includes('timeout')
    ) {
      return {
        content: `I'm currently unable to connect to the AI service. This could be due to high demand or a temporary outage. Please try again in a few minutes.`,
        model: model.apiName || model.id,
        tokensUsed: 0,
      };
    }
    if (error.message.includes('maximum context length')) {
      return {
        content: `I couldn't process your query with the knowledge base because the content is too large for my context window. Please try a more specific query or use a knowledge base with shorter documents.`,
        model: model.apiName || model.id,
        tokensUsed: 0,
      };
    }
    return {
      content: `Error: ${error.message || 'An unknown error occurred'}. Please try again or contact support if the issue persists.`,
      model: model.apiName || model.id,
      tokensUsed: 0,
    };
  }
}

export interface CsvStats {
  columns: string[];
  rowCount: number;
  numericSummary: Record<string, { count: number; min: number; max: number; mean: number }>;
}

export function computeCsvStats(csvText: string): CsvStats {
  const parsed = Papa.parse(csvText, { header: true, dynamicTyping: true });
  const rows = Array.isArray(parsed.data) ? (parsed.data as any[]).filter(r => r && Object.keys(r).length > 0) : [];
  const columns = (parsed.meta?.fields || []).filter(Boolean) as string[];
  const numericSummary: CsvStats['numericSummary'] = {};

  columns.forEach(col => {
    const values = rows.map(r => r[col]).filter(v => typeof v === 'number' && !Number.isNaN(v));
    if (values.length > 0) {
      const count = values.length;
      const min = Math.min(...values);
      const max = Math.max(...values);
      const mean = Number((values.reduce((a, b) => a + b, 0) / count).toFixed(2));
      numericSummary[col] = { count, min, max, mean };
    }
  });

  return {
    columns,
    rowCount: rows.length,
    numericSummary,
  };
}

export function buildFindingsMarkdown(stats: CsvStats, dateStr: string, extraNotes?: string): string {
  const { columns, rowCount, numericSummary } = stats;
  const lines: string[] = [];
  lines.push(`# Data Briefing - ${dateStr}`);
  lines.push('');
  lines.push(`Summary:`);
  lines.push(`- Rows: ${rowCount}`);
  lines.push(`- Columns: ${columns.length}`);
  lines.push(`- Columns: ${columns.join(', ') || 'N/A'}`);
  lines.push('');
  lines.push(`Key Findings:`);
  if (Object.keys(numericSummary).length > 0) {
    lines.push(`- Basic numeric summaries:`);
    Object.entries(numericSummary).forEach(([col, s]) => {
      lines.push(`  - ${col}: count=${s.count}, min=${s.min}, max=${s.max}, mean=${s.mean}`);
    });
  } else {
    lines.push(`- No numeric columns detected; review categorical distributions and text insights.`);
  }
  if (extraNotes?.trim()) {
    lines.push('');
    lines.push('Notes:');
    lines.push(extraNotes.trim());
  }
  lines.push('');
  lines.push('Suggested Charts:');
  if (Object.keys(numericSummary).length > 0) {
    const firstNumeric = Object.keys(numericSummary)[0];
    lines.push(`- Histogram of ${firstNumeric}`);
    lines.push(`- Box plot comparing ranges across numeric columns`);
    lines.push(`- Line chart for time-based columns (if present)`);
  } else {
    lines.push(`- Bar chart of category counts`);
    lines.push(`- Pie chart for categorical share`);
  }
  lines.push('');
  return lines.join('\n');
}

export async function generateFindingsFromCsv(csvText: string, dateStr: string): Promise<{ findingsMd: string; deckOutline: string }> {
  const stats = computeCsvStats(csvText);
  const findingsMd = buildFindingsMarkdown(stats, dateStr);

  const deckOutline = [
    `Title: Briefing ${dateStr}`,
    `Slide 1: Executive Summary`,
    `Slide 2: Dataset Overview (${stats.columns.join(', ') || 'unknown columns'})`,
    `Slide 3: Key Trends`,
    `Slide 4: Outliers & Anomalies`,
    `Slide 5: Recommendations`,
    `Slide 6: Next Steps`,
  ].join('\n');

  return { findingsMd, deckOutline };
}